import { create } from 'zustand'
import { ref as dbRef, onValue, set as dbSet, update, remove, get as dbGet, type Unsubscribe } from 'firebase/database'
import { collection, getDocs } from 'firebase/firestore'
import { rtdb, db } from '../firebase'

export interface SOSAlert {
  active: boolean
  triggeredAt: string
  location: { lat: number; lng: number } | null
  locationLabel?: string | null
  locationAddress?: string | null
  inZoneName?: string | null
  profileId: string | null
  profileName?: string | null
  uid?: string | null
  triggeredBy?: { uid: string; name: string } | null
}

export type ResolveOutcome = 'safe' | 'false_alarm' | 'escalated' | 'referred'

export interface ResolveAlertDetails {
  outcome: ResolveOutcome
  note: string
  resolvedByUid: string
  resolvedByName: string
  resolvedByEmail?: string | null
  source?: 'command_center' | 'guardian' | 'org'
}

function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

async function fetchZoneName(uid: string, lat: number, lng: number): Promise<string | null> {
  try {
    const snap = await getDocs(collection(db, 'users', uid, 'safeZones'))
    for (const d of snap.docs) {
      const z = d.data() as { name: string; lat: number; lng: number; radius: number }
      if (haversineM(lat, lng, z.lat, z.lng) <= z.radius) return z.name
    }
  } catch (err: unknown) {
    console.warn('[sos.zoneNameFor] safezone lookup failed:', err)
  }
  return null
}

interface SOSState {
  isSOSActive: boolean
  mySOSStatus: SOSAlert | null
  activeAlertsList: (SOSAlert & { uid: string })[]
  _uid: string | null
  _unsub: Unsubscribe | null
  _adminUnsub: Unsubscribe | null

  startListening: (uid: string) => void
  stopListening: () => void
  startAdminListening: () => void
  stopAdminListening: () => void
  triggerSOS: (profileId?: string, profileName?: string, location?: { lat: number; lng: number } | null) => Promise<void>
  cancelSOS: () => Promise<void>
  resolveAlert: (uid: string, details?: ResolveAlertDetails) => Promise<void>
}

export const useSOSStore = create<SOSState>()((set, get) => ({
  isSOSActive: false,
  mySOSStatus: null,
  activeAlertsList: [],
  _uid: null,
  _unsub: null,
  _adminUnsub: null,

  startListening(uid) {
    get()._unsub?.()
    const ref = dbRef(rtdb, `sos_status/${uid}`)
    const unsub = onValue(ref, (snap) => {
      const data = snap.exists() ? (snap.val() as SOSAlert) : null
      set({ mySOSStatus: data, isSOSActive: data?.active === true })
    })
    set({ _uid: uid, _unsub: unsub })
  },

  stopListening() {
    get()._unsub?.()
    set({ _uid: null, _unsub: null, mySOSStatus: null, isSOSActive: false })
  },

  startAdminListening() {
    get()._adminUnsub?.()
    const ref = dbRef(rtdb, 'sos_status')
    const unsub = onValue(ref, (snap) => {
      if (!snap.exists()) {
        set({ activeAlertsList: [] })
        return
      }
      const raw = snap.val() as Record<string, SOSAlert>
      const alerts = Object.entries(raw)
        .filter(([_, alert]) => alert.active)
        .map(([uid, alert]) => ({ ...alert, uid }))
      set({ activeAlertsList: alerts })
    })
    set({ _adminUnsub: unsub })
  },

  stopAdminListening() {
    get()._adminUnsub?.()
    set({ _adminUnsub: null, activeAlertsList: [] })
  },

  async triggerSOS(profileId, profileName, location) {
    const uid = get()._uid
    if (!uid) throw new Error('Not authenticated')

    // Guard against repeat/looping calls for the same profile's already-active alert —
    // scoped to profileId (not just "any SOS active") so a second dependent's SOS
    // isn't blocked while a different one is still unresolved.
    const existing = get().mySOSStatus
    if (existing?.active && existing.profileId === (profileId ?? null)) return

    const triggeredAt = new Date().toISOString()
    const initialLocation = location ?? null
    set({
      isSOSActive: true,
      mySOSStatus: {
        active: true,
        triggeredAt,
        location: initialLocation,
        locationLabel: null,
        profileId: profileId ?? null,
        profileName: profileName ?? null,
      },
    })

    // Write immediately so recording/notifications fire without waiting for geo.
    // On failure, roll back local state — "SOS active" with guardians never notified is the
    // worst failure mode this app can have; caller must see the rejection and re-trigger.
    try {
      await dbSet(dbRef(rtdb, `sos_status/${uid}`), {
        active: true,
        triggeredAt,
        location: initialLocation,
        locationLabel: null,
        profileId: profileId ?? null,
        profileName: profileName ?? null,
      } satisfies SOSAlert)
    } catch (err) {
      set({ isSOSActive: false, mySOSStatus: null })
      throw err
    }

    // If RN pre-fetched location, reverse-geocode label in background and return.
    if (initialLocation) {
      Promise.all([
        fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${initialLocation.lat}&lon=${initialLocation.lng}`, {
          headers: { 'Accept-Language': 'en' },
        }).then(r => r.json()) as Promise<{ display_name?: string; address?: Record<string, string | undefined> }>,
        fetchZoneName(uid, initialLocation.lat, initialLocation.lng),
      ])
        .then(([data, inZoneName]) => {
          const a = data.address ?? {}
          const city = a.city || a.town || a.municipality || a.suburb || a.village || a.county || ''
          const province = a.province || a.state || a.region || ''
          const country = a.country || ''
          const label = [city, province, country].filter(Boolean).join(', ')
          const patch: Record<string, string | null> = {}
          if (label) patch.locationLabel = label
          if (data.display_name) patch.locationAddress = data.display_name
          if (inZoneName) patch.inZoneName = inZoneName
          if (Object.keys(patch).length) update(dbRef(rtdb, `sos_status/${uid}`), patch).catch((err) => console.warn('[sos] status patch failed during active alert', err))
        })
        .catch(() => {})
      return
    }

    // Web: get location via browser geolocation API.
    if (typeof navigator === 'undefined' || !navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const location = { lat: pos.coords.latitude, lng: pos.coords.longitude }
        update(dbRef(rtdb, `sos_status/${uid}`), { location }).catch((err) => console.warn('[sos] location update failed during active alert', err))
        Promise.all([
          fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.lat}&lon=${location.lng}`, {
            headers: { 'Accept-Language': 'en' },
          }).then(r => r.json()) as Promise<{ display_name?: string; address?: Record<string, string | undefined> }>,
          fetchZoneName(uid, location.lat, location.lng),
        ])
          .then(([data, inZoneName]) => {
            const a = data.address ?? {}
            const city = a.city || a.town || a.municipality || a.suburb || a.village || a.county || ''
            const province = a.province || a.state || a.region || ''
            const country = a.country || ''
            const label = [city, province, country].filter(Boolean).join(', ')
            const patch: Record<string, string | null> = {}
            if (label) patch.locationLabel = label
            if (data.display_name) patch.locationAddress = data.display_name
            if (inZoneName) patch.inZoneName = inZoneName
            if (Object.keys(patch).length) update(dbRef(rtdb, `sos_status/${uid}`), patch).catch((err) => console.warn('[sos] status patch failed during active alert', err))
          })
          .catch((err: unknown) => { console.warn('[SOS] geocode failed:', err) })
      },
      (err: GeolocationPositionError) => { console.warn('[SOS] geolocation failed:', err) },
      { enableHighAccuracy: true, timeout: 5000 },
    )
  },

  async cancelSOS() {
    const uid = get()._uid
    if (!uid) throw new Error('Not authenticated')
    await remove(dbRef(rtdb, `sos_status/${uid}`))
  },

  async resolveAlert(uid, details) {
    if (details) {
      const statusRef = dbRef(rtdb, `sos_status/${uid}`)
      const statusSnap = await dbGet(statusRef)
      const current = statusSnap.exists() ? (statusSnap.val() as SOSAlert) : null
      const resolvedAt = new Date().toISOString()
      const historyKey = Date.now().toString()

      await dbSet(dbRef(rtdb, `sos_history/${uid}/${historyKey}`), {
        type: 'resolved',
        resolvedAt,
        resolvedBy: {
          uid: details.resolvedByUid,
          name: details.resolvedByName,
          email: details.resolvedByEmail ?? null,
        },
        resolution: {
          outcome: details.outcome,
          note: details.note.trim(),
          source: details.source ?? 'command_center',
        },
        alert: current ? {
          triggeredAt: current.triggeredAt,
          profileId: current.profileId ?? null,
          profileName: current.profileName ?? null,
          location: current.location ?? null,
          locationLabel: current.locationLabel ?? null,
          locationAddress: current.locationAddress ?? null,
          inZoneName: current.inZoneName ?? null,
        } : null,
      })
    }

    await remove(dbRef(rtdb, `sos_status/${uid}`))
  },
}))
